import React, { useMemo, useState } from "react";
import { NavLink } from 'react-router-dom';
import { requestPasswordReset } from '../features/auth/forgot-password/api/forgotPasswordApi';
import { getCurrentLanguageKey, getLanguageSection } from '../shared/services/languageService';

function validateEmail(email, language) {
  const normalizedEmail = email.trim();

  if (!normalizedEmail) {
    return language.requiredEmail;
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailPattern.test(normalizedEmail)) {
    return language.invalidEmail;
  }

  return '';
}

function ForgotPass() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const language = useMemo(() => getLanguageSection('forgotpassword'), []);
  const currentLanguage = getCurrentLanguageKey();

  const handleInputChange = (event) => {
    setEmail(event.target.value);
    setError('');
  };

  const restorepass = async () => {
    const trimmedEmail = email.trim();
    const validationError = validateEmail(trimmedEmail, language);

    if (validationError) {
      setError(validationError);
      setMsg('');
      return;
    }

    setError('');
    setMsg('');
    setIsSubmitting(true);

    try {
      await requestPasswordReset(trimmedEmail);
      setMsg('success');
      setError('');
      setEmail('');
    } catch (requestError) {
      setError(requestError?.payload?.errorMessage || requestError.message || language.genericError);
      setMsg('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="forgotpassword-page" className="mr-ui bg-[#f3f5fb] px-4 py-10 md:px-8">
      {msg === 'success' ? (
        <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-marival md:p-10">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-600">
            <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h1 className="m-0 text-3xl font-black leading-tight text-[#11183f]">{language.successTitle}</h1>
          <p className="mx-auto mb-0 mt-4 max-w-xl text-base leading-7 text-slate-600">{language.successText}</p>
          <p className="mx-auto mb-7 mt-3 max-w-xl text-sm leading-6 text-slate-500">{language.successHelp}</p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <NavLink
              to={`/login${currentLanguage ? `?lang=${currentLanguage}` : ''}`}
              className="inline-flex h-12 min-w-[190px] items-center justify-center rounded-xl bg-[#f6c833] px-5 text-sm font-black text-[#11183f] shadow-[0_12px_24px_rgba(246,200,51,0.26)] hover:bg-[#ffd84d]"
            >
              {language.backToLogin}
            </NavLink>
            <button
              type="button"
              className="inline-flex h-12 min-w-[190px] items-center justify-center rounded-xl border border-slate-200 bg-white px-5 text-sm font-black text-[#11183f] hover:bg-slate-50"
              onClick={() => setMsg('')}
            >
              {language.resend}
            </button>
          </div>
        </div>
      ) : (
        <div className="mx-auto grid w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-marival lg:grid-cols-[minmax(0,1fr)_430px]">
          <section className="border-b border-slate-200 bg-slate-50 p-8 md:border-b-0 md:border-r md:p-10">
            <div className="mb-8 flex h-14 w-14 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500">
              <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M7 11V8a5 5 0 0 1 10 0v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M5 11h14v9H5v-9Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="mb-4 text-xs font-black uppercase text-slate-500">{language.eyebrowLabel}</div>
            <h1 className="m-0 text-3xl font-black leading-tight text-[#11183f] md:text-4xl">{language.forgot}</h1>
            <p className="mb-0 mt-4 max-w-md text-base leading-7 text-slate-600">{language.text1}</p>
            <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5">
              <h2 className="m-0 mb-2 text-base font-black text-[#11183f]">{language.panelTitle}</h2>
              <p className="m-0 text-sm leading-6 text-slate-600">{language.panelText}</p>
            </div>
          </section>

          <section className="p-6 md:p-8">
            <p className="m-0 mb-6 text-sm font-black text-[#f5660b]">{language.text2}</p>

            {error ? (
              <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-700">
                {error}
              </div>
            ) : null}

            <div className="mb-6">
              <label className="mb-2 block text-sm font-black text-[#11183f]">{language.email}</label>
              <input
                type="email"
                className="mr-login-input h-12 w-full rounded-[10px] border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-[#11183f] shadow-none"
                autoComplete="off"
                placeholder={language.emailPlaceholder}
                value={email}
                onChange={handleInputChange}
                name="forgotpassword.email"
              />
            </div>

            <button
              onClick={restorepass}
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border-0 bg-[#f6c833] text-sm font-black text-[#11183f] shadow-[0_12px_24px_rgba(246,200,51,0.26)] hover:bg-[#ffd84d] disabled:cursor-wait disabled:opacity-70"
              disabled={isSubmitting}
            >
              <span>{isSubmitting ? language.sending : language.sendbtn}</span>
            </button>
          </section>
        </div>
      )}
    </div>
  );
}

export default ForgotPass;
